import { SynchronizationStatus, SynchronizationStatusEnum } from '@jupiter/shared';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import SyncIcon from '@material-ui/icons/Sync';
import { formatDistance } from 'date-fns';
import { enUS, pt } from 'date-fns/locale';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SynchronizationDialog from '../../components/dialogs/synchronization-dialog';
import useNotifications from '../../redux/notifications/notifications.hooks';
import useSynchronizationDialog from '../../redux/synchronization-dialog/synchronization-dialog.hooks';
import {
  useGetSynchronizationStatusQuery,
  useTriggerSynchronizationMutation,
} from '../../redux/synchronization/synchronization.api';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    '@keyframes syncSpin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(360deg)',
      },
    },
    root: {},
    button: {
      width: '100%',
      border: '1px solid',
      borderRadius: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.dark,
      display: 'flex',
    },
    buttonInProgress: {
      borderColor: theme.palette.info.main,
      backgroundColor: theme.palette.info.main,
      color: theme.palette.common.white,
    },
    iconInProgress: {
      animation: '$syncSpin 1s linear infinite',
    },
    buttonTitle: {
      fontWeight: 600,
      fontSize: '14px',
      textTransform: 'none',
      textAlign: 'left',
    },
    buttonSubText: {
      fontSize: '10px',
      textTransform: 'none',
      textAlign: 'left',
    },
  }),
);

function ImportButtonSyncronizationStatusText({ syncStatus }: { syncStatus: SynchronizationStatus | undefined }) {
  const lastSyncAt = syncStatus?.lastSyncAt;
  const status = syncStatus?.status;
  const { t, i18n } = useTranslation('common');

  if (status === SynchronizationStatusEnum.IN_PROGRESS) {
    return <>{t('sidebar.synchronization.status-running')}</>;
  }

  if (!lastSyncAt) {
    return <>{t('sidebar.synchronization.status-never')}</>;
  }

  return (
    <Trans
      i18nKey="sidebar.synchronization.last-synced-at"
      values={{
        date: formatDistance(new Date(lastSyncAt), new Date(), {
          addSuffix: true,
          locale: i18n.language === 'pt' ? pt : enUS,
        }),
      }}
    />
  );
}

export default function NavImportButton() {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const [pollStatus, setPollStatus] = useState(false);
  const { data, isLoading: synchronizationStatusLoading } = useGetSynchronizationStatusQuery(undefined, {
    pollingInterval: pollStatus ? 2500 : 0,
  });

  const [triggerSync, { error: triggerSyncError, isSuccess: triggerSyncSuccess, isLoading: triggerSyncLoading }] =
    useTriggerSynchronizationMutation({ fixedCacheKey: 'synchronization' });
  const { openDialog } = useSynchronizationDialog();
  const { show } = useNotifications();

  const canOpenDialog = useMemo(() => {
    if (!data || synchronizationStatusLoading || triggerSyncLoading) {
      return false;
    }

    return data.status !== SynchronizationStatusEnum.IN_PROGRESS;
  }, [data, synchronizationStatusLoading, triggerSyncLoading]);

  const isInProgress = data?.status === SynchronizationStatusEnum.IN_PROGRESS;

  useEffect(() => {
    if (triggerSyncSuccess) {
      show({ message: t('sidebar.synchronization.message-triggered-success'), severity: 'success' });
    }
  }, [triggerSyncSuccess, show, t, setPollStatus]);

  useEffect(() => {
    if (triggerSyncError) {
      if ('status' in triggerSyncError) {
        const status = triggerSyncError.status;

        if (status === 428) {
          openDialog();
          return;
        }
      }

      show({ message: t('common.error-unknown'), severity: 'error' });
    }
  }, [triggerSyncError, openDialog, show, t, setPollStatus]);

  useEffect(() => {
    if (data?.status !== SynchronizationStatusEnum.IN_PROGRESS) {
      setPollStatus(false);
    } else {
      setPollStatus(true);
    }
  }, [data?.status, setPollStatus]);

  const handleUpdateButtonClick = () => {
    if (!canOpenDialog) {
      return;
    }

    triggerSync(undefined);
  };

  return (
    <div className={classes.root}>
      <ButtonBase
        focusRipple
        className={`${classes.button} ${isInProgress ? classes.buttonInProgress : ''}`}
        onClick={handleUpdateButtonClick}
        disabled={!canOpenDialog}
      >
        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="left" width="100%">
          <Box mr={2} display="flex" alignItems="center">
            <SyncIcon className={isInProgress ? classes.iconInProgress : undefined} />
          </Box>
          <Box>
            <Typography variant="subtitle1" className={classes.buttonTitle}>
              {t('sidebar.synchronization.title')}
            </Typography>
            <Typography variant="subtitle1" className={classes.buttonSubText}>
              <ImportButtonSyncronizationStatusText syncStatus={data} />
            </Typography>
          </Box>
        </Box>
      </ButtonBase>
      <SynchronizationDialog />
    </div>
  );
}
